 import { useEffect, useMemo, useState } from "react";
 import { Accordion, AccordionTab } from "primereact/accordion";
 import useAsyncEffect from "../useAsyncEffect";
 import useForm from "../use-form/useForm";
 import useCatalogSubform from "../useCatalogSubform";
 import useCatalogTable from "../use-catalog-table/useCatalogTable";
 import ButtonAccordionHeader from "../../components/Core/button-accordion-header/ButtonAccordionHeader";
 import agent from "../../agent";
 import styles from "./style.module.scss";

const taxTableConfig = [
    {
        field: "id",
        header: "ID",
    },
    {
        field: "nameRu",
        header: "Вид налога/платежа"
    },
    {
        field: "basicValue",
        header: "Базовая величина"
    },
    {
        field: "amountIndicat",
        header: "Размер"
    },
    {
        field: "maxIncome",
        header: "Макс-ный доход, кол-во МЗП"
    },
    {
        field: "maxSum",
        header: "Сумма макс."
    },
    {
        field: "minIncome",
        header: "Мин-ный доход, кол-во МЗП"
    },
    {
        field: "minSum",
        header: "Сумма мин."
    },
]

const useCatalogForm = (
    { control, errors, watch, setValue },
    type = "monitoring", code = ""
) => {
    const rows = useMemo(() => [
        code === "operation-types" && [
            {
                name: "cipher",
                label: "Шифр",
                required: true,
                placeholder: "Введите обозначение"
            },
            {
                name: "startedAt",
                label: "Дата начала действия",
                required: true,
                placeholder: "ДД.ММ.ГГГГ",
                inputType: "date",
            },
            {
                name: "endedAt",
                label: "Дата окончания действия",
                required: false,
                placeholder: "ДД.ММ.ГГГГ",
                inputType: "date",
            },
        ],
        [
            {
                name: "name",
                label: code === "operation-types"
                    ? "Наименование типа операции"
                    : "Наименование",
                required: true,
                placeholder: code === "indicators"
                    ? "Выберите значение"
                    : "Введите наименование",
                inputType: code === "indicators" ? "dropdown" : "text",
                onOptionSelect: code === "indicators"
                    ? (option) => {
                        const shortName = option?.sign ?? "";

                        setValue("shortName", shortName);
                    }
                    : null
            },
        ],
        type === "billing" && code !== "operation-types" && [
            {
                name: "shortName",
                label: "Краткое обозначение",
                required: code !== "indicators",
                disabled: code === "indicators",
                placeholder: code === "indicators"
                    ? "Автозаполнение"
                    : "Введите краткое обозначение"
            },
            code === "indicators" && {
                name: "cipher",
                label: "Шифр",
                required: true,
                placeholder: "Введите шифр"
            },
        ],
        type === "billing" && !(code === "basic-values" || code === "operation-types")
        && [
            {
                name: "basicValue",
                label: "Базовая величина",
                placeholder: "Выберите значение",
                inputType: "dropdown",
                valueMode: "object",
                labelField: "sign",
                onOptionSelect: (option) => {
                    setValue("basicValue", option || null);
                }
            },
                {
                    name: "amountIndicat",
                    label: "Размер",
                    required: true,
                    placeholder: "Введите размер",
                    inputType: "number",
                },
        ],
        type === "billing" && !(code === "basic-values" || code === "operation-types")
        && [
            {
                name: "startedAt",
                label: "Дата начала действия",
                required: true,
                placeholder: "ДД.ММ.ГГГГ",
                inputType: "date",
            },
            {
                name: "endedAt",
                label: "Дата окончания действия",
                required: false,
                placeholder: "ДД.ММ.ГГГГ",
                inputType: "date",
            },
        ],
        code === "taxes" && [
            {
                name: "maxIncome",
                label: "Максимальный доход, количество МЗП",
                required: true,
                placeholder: "Введите количество МЗП",
                inputType: "number",
            },
            {
                name: "minIncome",
                label: "Минимальный доход, количество МЗП",
                required: true,
                placeholder: "Введите количество МЗП",
                inputType: "number",
            },
        ],
        type === "billing" && code !== "basic-values" && [
            {
                name: "activity",
                label: "Активен",
                required: true,
                inputType: "boolean",
            }
        ]
    ]
        .filter(row => row && typeof row === "object")
        .map(row => row.filter(elem => typeof elem === "object"))
        .filter(row => row.length),
    [code, type, setValue]
    );

    const [tableData, setTableData] = useState([]);

    const operationTypeTaxes = watch("operationTypeTaxes");
    const tax = watch("tax");
    const form = useForm(code, rows, { control, errors });
    const subform = useCatalogSubform(code, {
        control, errors, setValue
    });
    const taxTable = useCatalogTable(tableData, taxTableConfig);

    const handleTaxSave = () => {
        if (!watch("tax")) {
            return;
        }

        let obj = {};

        taxTableConfig.map(item => item.field).forEach((field) => {
            obj = {
                ...obj,
                [field]: (field === "id" || field === "nameRu")
                    ? (watch("tax"))[field]
                    : watch(field)
            }
        })

        setTableData(prev => [...prev, obj]);

        const clearInputNames = [
            [ "tax", "basicValue", "taxStartedAt", "taxEndedAt" ],
            ["amountIndicat", "maxIncome", "minIncome", "maxSum", "minSum"],
            ["taxActivity"]
        ];

        clearInputNames.forEach((row, id) => {
            let initialValue;

            switch (id) {
                case 0:
                    initialValue = null;
                    break;
                case 1:
                    initialValue = "";
                    break;
                case 2:
                    initialValue = true;
                    break;
                default:
                    initialValue = null;
                    break;
            }

            row.forEach((item) => {
                setValue(item, initialValue);
            })
        })
    }

    useAsyncEffect(async () => {
        const loadTaxes = async () => {
            if (!operationTypeTaxes || !operationTypeTaxes.length) {
                setTableData([]);
                return;
            }

            try {
                const rows = await Promise.all(
                    operationTypeTaxes.map(async ({ taxId, maxSum, minSum }) => {
                        const tax = await agent.AccountingDictionary.getById("taxes", taxId);

                        return {
                            id: tax.id,
                            nameRu: tax.nameRu,
                            basicValue: tax.basicValue?.sign ?? "",
                            amountIndicat: tax.amountIndicat,
                            maxIncome: tax.maxIncome,
                            minIncome: tax.minIncome,
                            maxSum,
                            minSum,
                        };
                    })
                );

                setTableData(rows);
            } catch (e) {
                console.error("Failed to load taxes for operation type", e);
                setTableData([]);
            }
        };

        await loadTaxes();
    }, [operationTypeTaxes]);

    useEffect(() => {
        setValue("newOperationTypeTaxes", tableData
            .map(({ id, maxSum, minSum }) => ({ taxId: id, maxSum, minSum }))
        );
    }, [tableData]);

    return (
        <>
            {form}
            {code === "operation-types" && (
                <div className={styles.operationTypesContainer}>
                    <Accordion activeIndex={0}>
                        <AccordionTab className={styles.taxAccordionTab}
                                      headerTemplate={
                                        <ButtonAccordionHeader onClick={handleTaxSave}
                                                               disabled={tax == null}
                                        />
                                      }
                        >
                            {subform}
                        </AccordionTab>
                    </Accordion>
                    {taxTable}
                </div>
            )}
        </>
    )
}

export default useCatalogForm;